import { useEffect, useState } from "react";
import type { Theme } from "@/lib/theme";

const THEMES: readonly string[] = ["sunset", "dawn"];

function readTheme(): Theme {
  const value = document.documentElement.getAttribute("data-theme");
  return THEMES.includes(value ?? "") ? (value as Theme) : "sunset";
}

/**
 * The theme, as React sees it.
 *
 * `startThemeRuntime` (`lib/theme.ts`) is the owner: it writes `data-theme`
 * on `<html>` before React mounts, re-writes it at 06:00/18:00 and on
 * `visibilitychange`, and honours the stored override. This hook only reads
 * that attribute — it never resolves the clock itself, so there is exactly
 * one answer to "what time of day is it" in the app.
 *
 * Needed because two consumers take the theme as a *value*, not as CSS:
 * `ScreenFrame`'s `data-theme` and the dot-work engine, which bakes its
 * tone tables per theme.
 */
export function useDocumentTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    const sync = (): void => setTheme(readTheme());
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    // The runtime may have flipped between the first render and this effect.
    sync();
    return () => observer.disconnect();
  }, []);

  return theme;
}
